// The three seats and the models each can run.
//
// Starting credit runs one model per seat, the one marked `starter`: the best of each,
// Opus 5.5, Sol 6.1 and Kimi K3. A new account costs at most its grant whichever it
// runs, and the best models make the best first brawl; they also spend the grant in a
// few brawls, where cheap ones (Haiku 5.5, Luna 6) would make it last for weeks and
// never ask for a payment. The others unlock once the account has added credit.
//
// Every model here is one Kottos AI serves from Amazon Bedrock; `id` is the Kottos
// product id, and the Worker sends it as the step that pins the model
// (worker/kottos.js). Prices are list USD per million tokens (input, output) from
// Kottos's price table (quotes.json): Bedrock's global rates where the route has a
// global profile, else the us./in-region rates (GPT-5.5, GPT-5.4 and the open-weights
// models). Opus 4.6, Opus 4.5 and Sonnet 4.5 are left out: Bedrock serves them only on
// its invoke door, which Kottos signs with SigV4, and the Worker's key is a bearer key. Turns are charged at what Kottos states each call cost,
// plus the Worker's margin. Every model answered on its Bedrock route on 2026-10-26.
// Fable 5 and 5.1 are left out: Bedrock refuses them until the AWS account opts in to
// keeping prompts 30 days. Grok is not open weights; it sits in the third seat for now.

export const SEATS = {
  ANTHROPIC: {
    label: "Anthropic",
    defaultModel: "claude-opus-5-5",
    models: [
      { id: "claude-opus-5-5", name: "Opus 5.5", price: [4, 20], note: "Recommended: strong reasoning at a fair price", starter: true },
      { id: "claude-sonnet-5-5", name: "Sonnet 5.5", price: [2, 10], note: "Faster, half Opus's price" },
      { id: "claude-haiku-5-5", name: "Haiku 5.5", price: [0.1, 0.5], note: "Fastest and cheapest" },
      { id: "claude-opus-5", name: "Opus 5", price: [5, 25], note: "The previous Opus" },
      { id: "claude-sonnet-5", name: "Sonnet 5", price: [2, 10], note: "The previous Sonnet" },
      { id: "claude-opus-4-8", name: "Opus 4.8", price: [5, 25], note: "Claude 4 generation" },
      { id: "claude-opus-4-7", name: "Opus 4.7", price: [5, 25], note: "Claude 4 generation" },
      { id: "claude-sonnet-4-6", name: "Sonnet 4.6", price: [3, 15], note: "Claude 4 generation" },
      { id: "claude-haiku-4-5", name: "Haiku 4.5", price: [1, 5], note: "The previous Haiku" },
    ],
  },
  OPENAI: {
    label: "OpenAI",
    defaultModel: "gpt-6.1-sol",
    models: [
      { id: "gpt-6.1-sol", name: "Sol 6.1", price: [2, 10], note: "Recommended: OpenAI's current all-rounder", starter: true },
      { id: "gpt-6.1-terra", name: "Terra 6.1", price: [1, 5], note: "The current Terra, half Sol's price" },
      { id: "gpt-6-astra", name: "Astra 6", price: [10, 50], note: "OpenAI's largest; 5× Sol's price" },
      { id: "gpt-6-sol", name: "Sol 6", price: [2, 10], note: "The previous Sol" },
      { id: "gpt-6-luna", name: "Luna 6", price: [0.1, 0.5], note: "Fastest and cheapest" },
      { id: "gpt-5.6-sol", name: "Sol 5.6", price: [4, 20], note: "GPT-5.6 generation" },
      { id: "gpt-5.6-terra", name: "Terra 5.6", price: [2, 12], note: "GPT-5.6 generation" },
      { id: "gpt-5.6-luna", name: "Luna 5.6", price: [0.2, 1.2], note: "GPT-5.6 generation, small and fast" },
      { id: "gpt-5.5", name: "GPT-5.5", price: [5.5, 33], note: "An earlier GPT" },
      { id: "gpt-5.4", name: "GPT-5.4", price: [2.75, 16.5], note: "The earliest GPT on Bedrock" },
    ],
  },
  OPEN: {
    label: "Open weights",
    defaultModel: "gpt-oss-20b",
    models: [
      { id: "gpt-oss-20b", name: "gpt-oss 20B", price: [0.07, 0.3], note: "Recommended: OpenAI's small open-weights model, fast and the cheapest of the pair" },
      { id: "gpt-oss-120b", name: "gpt-oss 120B", price: [0.15, 0.6], note: "The larger one, a little slower" },
      { id: "kimi-k3", name: "Kimi K3", price: [3, 15], note: "Moonshot's newest; the strongest open model here, and the dearest", starter: true },
      { id: "grok-4.7", name: "Grok 4.7", price: [2, 6], note: "xAI's newest; reasons before it answers. Not open weights, listed here for now" },
      { id: "grok-4.6", name: "Grok 4.6", price: [2, 6], note: "The previous Grok. Not open weights either" },
      { id: "kimi-k2.5", name: "Kimi K2.5", price: [0.6, 3], note: "The previous Kimi, a fifth of K3's price" },
      { id: "kimi-k2-thinking", name: "Kimi K2 Thinking", price: [0.6, 2.5], note: "Moonshot's reasoning Kimi K2; thinks at length, so it's slow" },
      { id: "glm-5", name: "GLM-5", price: [1, 3.2], note: "Z.ai's flagship" },
      { id: "glm-4.7", name: "GLM-4.7", price: [0.6, 2.2], note: "The previous GLM" },
      { id: "glm-4.7-flash", name: "GLM-4.7 Flash", price: [0.07, 0.4], note: "Z.ai's small and fast one" },
      { id: "deepseek-v3.2", name: "DeepSeek V3.2", price: [0.62, 1.85], note: "DeepSeek's current general model" },
      { id: "minimax-m2.5", name: "MiniMax M2.5", price: [0.3, 1.2], note: "Reasons before it answers; slower" },
      { id: "minimax-m2.1", name: "MiniMax M2.1", price: [0.3, 1.2], note: "The previous MiniMax" },
      { id: "minimax-m2", name: "MiniMax M2", price: [0.3, 1.2], note: "The first MiniMax M2" },
      { id: "qwen3-vl-235b-a22b", name: "Qwen3 235B", price: [0.53, 2.66], note: "Alibaba's largest Qwen3 on Bedrock" },
      { id: "qwen3-next-80b-a3b", name: "Qwen3 Next 80B", price: [0.14, 1.2], note: "Alibaba's efficient mixture of experts" },
      { id: "qwen3-32b", name: "Qwen3 32B", price: [0.15, 0.6], note: "A dense mid-size Qwen3" },
      { id: "qwen3-coder-next", name: "Qwen3 Coder Next", price: [0.5, 1.2], note: "Qwen tuned for code" },
      { id: "qwen3-coder-30b-a3b", name: "Qwen3 Coder 30B", price: [0.15, 0.6], note: "The smaller Qwen coder" },
      { id: "mistral-large-3-675b", name: "Mistral Large 3", price: [0.5, 1.5], note: "Mistral's flagship" },
      { id: "devstral-2-123b", name: "Devstral 2", price: [0.4, 2], note: "Mistral tuned for code" },
      { id: "magistral-small-2509", name: "Magistral Small", price: [0.5, 1.5], note: "Mistral's reasoning model" },
      { id: "ministral-3-14b", name: "Ministral 14B", price: [0.2, 0.2], note: "Mistral's small one" },
      { id: "ministral-3-8b", name: "Ministral 8B", price: [0.15, 0.15], note: "Smaller still" },
      { id: "ministral-3-3b", name: "Ministral 3B", price: [0.1, 0.1], note: "Mistral's tiniest" },
      { id: "voxtral-small-24b", name: "Voxtral Small", price: [0.1, 0.3], note: "Mistral's speech model, answering text here" },
      { id: "voxtral-mini-3b", name: "Voxtral Mini", price: [0.04, 0.04], note: "The small Voxtral; the cheapest here" },
      { id: "nemotron-super-3-120b", name: "Nemotron Super 3", price: [0.15, 0.65], note: "NVIDIA's large open model" },
      { id: "nemotron-nano-3-30b", name: "Nemotron Nano 3", price: [0.06, 0.24], note: "NVIDIA's small one" },
      { id: "nemotron-nano-12b-v2", name: "Nemotron Nano 12B v2", price: [0.2, 0.6], note: "NVIDIA's vision-language Nano" },
      { id: "nemotron-nano-9b-v2", name: "Nemotron Nano 9B v2", price: [0.06, 0.23], note: "NVIDIA's smallest", systemSuffix: "/no_think" },
      { id: "gemma-3-27b", name: "Gemma 3 27B", price: [0.23, 0.38], note: "Google's open model" },
      { id: "gemma-3-12b", name: "Gemma 3 12B", price: [0.09, 0.29], note: "The middle Gemma" },
      { id: "gemma-3-4b", name: "Gemma 3 4B", price: [0.04, 0.08], note: "The small Gemma" },
    ],
  },
};

export const SEAT_IDS = Object.keys(SEATS);

// `systemSuffix`: a line a model needs at the end of its system prompt. Nemotron Nano 9B
// v2 writes its reasoning into the answer unless told "/no_think" (it ignores
// reasoning_effort; checked 2026-10-26).
export const systemFor = (model, system) => (model.systemSuffix ? `${system}\n${model.systemSuffix}` : system);

// The judge reads the verdicts after each round and says whether they give the same
// answer: when the votes split but all three agree, it ends the brawl. The user picks
// it from these models. Luna 6 is the default: on four judge cases with a known answer,
// run through Kottos on 2026-10-26, it was right on all four at 0.9 s every call, where
// Gemma 3 27B, the previous default, got one wrong and took 3 to 12 s. A judge never
// speaks in the debate, so the seat lock doesn't apply to it: every cheap judge runs on
// starting credit, and only Grok 4.7 (slow even at low effort) waits for credit, with
// the default judging for it until then.
export const JUDGES = [
  ["OPENAI", "gpt-6-luna"],
  ["ANTHROPIC", "claude-haiku-5-5"],
  ["OPEN", "gpt-oss-20b"],
  ["OPEN", "gpt-oss-120b"],
  ["OPEN", "gemma-3-27b"],
  ["OPEN", "grok-4.7"],
].map(([seat, id]) => SEATS[seat].models.find((m) => m.id === id));
export const JUDGE_MODEL = JUDGES[0]; // Luna 6, the default
// Whether starting credit runs a judge: the cheap ones do.
export const judgeFree = (m) => m.price[1] <= 0.65;
export const JUDGE_STARTER = JUDGES.find(judgeFree); // what a locked account's judge is: the default
export const judgeFor = (id) => JUDGES.find((m) => m.id === id) ?? JUDGE_MODEL;
// The seat a model belongs to (its provider, which decides the output-cap field).
export const seatOf = (model) => SEAT_IDS.find((seat) => SEATS[seat].models.includes(model));

// The chosen model for a seat, or the seat's default when the id is unknown.
export function modelFor(seat, id) {
  const s = SEATS[seat];
  return s.models.find((m) => m.id === id) ?? s.models.find((m) => m.id === s.defaultModel);
}

// The seat's starter model: what a locked seat runs instead of its pick.
export const starterFor = (seat) => {
  const { models, defaultModel } = SEATS[seat];
  return models.find((m) => m.id === defaultModel && m.starter) ?? models.find((m) => m.starter);
};

// "Opus 5.5, Sol 6.1 and Kimi K3": one starter per seat, for messages.
export const STARTER_NAMES = (() => {
  const names = SEAT_IDS.map((seat) => starterFor(seat).name);
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
})();

// Display names for a set of choices: { ANTHROPIC: "Opus 5.5", … }.
export function namesFor(choices = {}) {
  return Object.fromEntries(SEAT_IDS.map((seat) => [seat, modelFor(seat, choices[seat]).name]));
}
